import { useState, useMemo, useContext } from "react";
import { Box, Select, MenuItem, Stack } from "@mzc-pdc/ui";
import AdaptivePopper from "@components_v2/adaptive-popper/adaptive-popper";
import { NotifyHeader } from "./components/notify-header";
import { NotifyItem } from "./components/notify-item";
import { NotifyEmpty } from "./components/notify-empty";
import { NotifyItemSkeleton } from "./components/notify-item-skeleton";
import { Typography } from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconArrowDropDown } from "@mzc-cloudplex/icons";
import { isLocal, isDev } from "@cores/enviromentExtension";
import { generateTestProjects, getAllTestNotifications } from "@features/notify-popper/test-notify";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { updateNotifyStatus } from "@modules/notify";
import { WebSocketContext } from "@routes/providers/webSocketProvider";
import { useCallback } from "react";

export const NotifyPopper = ({ open, anchorEl, onClickClose, projects }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [isUnreadOnly, setIsUnreadOnly] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState("all");
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    //Note: 테스트 모드 활성화/비활성화 상태 관리
    const [useTestData, setUseTestData] = useState(false);
    const [selectedTestCase, setSelectedTestCase] = useState("allDomains");
    const isDevEnvironment = isLocal || isDev;

    const notify = useSelector(({ notify }) => notify);

    const { sendMessage, isConnected } = useContext(WebSocketContext);

    const handleToggleUnread = useCallback((checked) => {
        setIsUnreadOnly(checked);
    }, []);

    const handleProjectChange = useCallback((event) => {
        setSelectedProjectId(event.target.value);
    }, []);

    const handleClickAway = useCallback(() => {
        // Select가 열려있으면 onClickAway 무시
        if (isSelectOpen) {
            return;
        }
        onClickClose();
    }, [isSelectOpen, onClickClose]);

    const handleToggleTestMode = useCallback(() => {
        setUseTestData(!useTestData);
    }, [useTestData]);

    const handleTestCaseChange = useCallback((testCase) => {
        setSelectedTestCase(testCase);
    }, []);

    const sendPutMessageStatus = (status, targets) => {
        if (!sendMessage || !isConnected) return;

        targets.forEach((target) => {
            sendMessage?.({
                action: "putMessageStatus",
                data: { status, target },
            });
            console.log("sendPutMessageStatus", status, target);
        });
    };

    // 개별 알림의 읽음/안읽음 상태 토글
    const onHandleClickToggleReadButton = (item) => {
        if (!sendMessage || !isConnected) return;

        const newStatus = item.status === "UNREAD" ? "READ" : "UNREAD";

        const target = {
            receivedAt: item.receivedAt,
            traceId: item.traceId,
        };
        sendPutMessageStatus(newStatus, [target]);
        dispatch(updateNotifyStatus({ status: newStatus, targets: [target] }));
    };

    const onHandleClickMarkAllAsRead = () => {
        if (!sendMessage || !isConnected || !actualNotifyData) return;

        const unreadItems = actualNotifyData.filter((item) => item.status === "UNREAD");

        const targets = unreadItems.map((item) => ({
            receivedAt: item.receivedAt,
            traceId: item.traceId,
        }));
        sendPutMessageStatus("READ", targets);
        dispatch(updateNotifyStatus({ status: "READ", targets }));
    };

    const notifyData = useMemo(() => notify?.data, [notify?.data]);

    const actualNotifyData = useMemo(() => {
        if (isDevEnvironment && useTestData) {
            const generatedData = getAllTestNotifications()[selectedTestCase];
            return generatedData ? generatedData : notifyData;
        }
        return notifyData;
    }, [notifyData, useTestData, selectedTestCase, isDevEnvironment]);

    const actualProjects = useMemo(() => {
        if (isDevEnvironment && useTestData) {
            return generateTestProjects();
        }
        return projects;
    }, [projects, useTestData, isDevEnvironment]);

    const filteredNotifyData = useMemo(() => {
        let result = actualNotifyData || [];

        if (isUnreadOnly) {
            result = result.filter((msg) => msg.status === "UNREAD");
        }

        if (selectedProjectId && selectedProjectId !== "all") {
            result = result.filter((msg) => msg.data?.project?.id === selectedProjectId);
        }

        return result;
    }, [actualNotifyData, isUnreadOnly, selectedProjectId]);

    return (
        <AdaptivePopper
            open={open}
            anchorEl={anchorEl}
            onClickAway={handleClickAway}
            minHeight={400}
            maxHeight={1000}
            wrapperSx={{
                width: "656px",
                borderRadius: theme.spacing(1),
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Box data-role="header">
                <NotifyHeader
                    onToggleUnread={handleToggleUnread}
                    isUnreadOnly={isUnreadOnly}
                    onClose={onClickClose}
                    onClickMarkAllAsRead={onHandleClickMarkAllAsRead}
                    isDevEnvironment={isDevEnvironment}
                    useTestData={useTestData}
                    onToggleTestMode={handleToggleTestMode}
                    selectedTestCase={selectedTestCase}
                    onTestCaseChange={handleTestCaseChange}
                />

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.greySecondary[100]}`,
                    }}
                >
                    <Box sx={{ flex: 1, maxWidth: "307px" }}>
                        <Select
                            value={selectedProjectId}
                            onChange={handleProjectChange}
                            size="small"
                            fullWidth
                            IconComponent={IconArrowDropDown}
                            onOpen={() => setIsSelectOpen(true)}
                            onClose={() => setIsSelectOpen(false)}
                            MenuProps={{
                                slotProps: {
                                    root: {
                                        "data-popper-menu": true,
                                    },
                                },
                            }}
                            sx={{
                                minHeight: "32px",
                                backgroundColor: theme.palette.common.white,
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: theme.palette.greySecondary.light,
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: theme.palette.primary.main,
                                },
                                "& .MuiSelect-select": {
                                    py: 0.5,
                                    pr: 1.5,
                                    pl: 2,
                                },
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <MenuItem value="all">
                                <Typography
                                    variant="body1_400"
                                    sx={{
                                        color: theme.palette.greySecondary.main,
                                        fontSize: "14px",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {t("common::label::All projects", "All projects")}
                                </Typography>
                            </MenuItem>
                            {actualProjects?.map((project) => (
                                <MenuItem key={project.id} value={project.id}>
                                    <Typography
                                        variant="body1_400"
                                        sx={{
                                            color: theme.palette.greySecondary.main,
                                            fontSize: "14px",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {project.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>
            </Box>

            <Stack data-role="content" direction="column" sx={{ flex: 1 }}>
                {notify.pending ? (
                    Array.from({ length: 5 }).map((_, index) => <NotifyItemSkeleton key={`skeleton-${index + 1}`} />)
                ) : filteredNotifyData.length === 0 ? (
                    <NotifyEmpty />
                ) : (
                    filteredNotifyData.map((item) => (
                        <NotifyItem
                            key={`${item.traceId}-${item.receivedAt}`}
                            item={item}
                            onClickToggleReadButton={onHandleClickToggleReadButton}
                            onClickClose={onClickClose}
                        />
                    ))
                )}
            </Stack>
        </AdaptivePopper>
    );
};
